import { useRef, useState } from 'react';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import { useToast } from '../ui/Toast';
import { downloadFileFromUrl } from '../../lib/downloadFile';
import { DownloadIcon, UploadIcon } from '../ui/icons';

function formatDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
}

/**
 * v1.18.0 — investor-facing "Other Documents" checklist, deliberately
 * rendered as its own section visually distinct from the identity
 * submission above it (separate Card, no shared status badge, no shared
 * "Verified" language) per the spec's explicit rule: these documents never
 * gate anything and must never read as part of the identity approval gate.
 * Each row: template download (if one exists), current upload status, and
 * an upload/replace control — uploading again always just re-runs the same
 * POST /investor/kyc/documents call (no "resubmit because it changed"
 * prompt exists anywhere in this app, and it never will per the spec).
 *
 * "Download template" is its own full-visibility Button, not a small
 * inline link — easy to miss a text link tucked next to Upload, and a
 * plain `<a>` doesn't reliably download anyway (see lib/downloadFile.js).
 */
export default function OtherDocumentsChecklist({ items, templates, onUpload, isUploading }) {
  if (!items || items.length === 0) return null;

  const templateByTag = new Map((templates ?? []).map((t) => [t.tag, t]));

  return (
    <div className="space-y-3">
      {items.map((item) => (
        <ChecklistRow
          key={item.tag}
          item={item}
          template={templateByTag.get(item.tag)}
          onUpload={onUpload}
          isUploading={isUploading}
        />
      ))}
    </div>
  );
}

function ChecklistRow({ item, template, onUpload, isUploading }) {
  const toast = useToast();
  const [busyUpload, setBusyUpload] = useState(false);
  const [busyDownload, setBusyDownload] = useState(false);
  const inputRef = useRef(null);
  const isUploaded = item.status === 'uploaded';
  const uploadedDate = formatDate(item.uploadedAt);
  const hasTemplate = !!template?.downloadUrl;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusyUpload(true);
    try {
      await onUpload(item.tag, file);
    } finally {
      setBusyUpload(false);
    }
  };

  const handleDownload = async () => {
    setBusyDownload(true);
    const result = await downloadFileFromUrl(template.downloadUrl, `${item.label}.pdf`);
    setBusyDownload(false);
    if (!result.ok) {
      toast.push({ tone: 'danger', title: 'Download failed', message: "Couldn't download this template. Please try again." });
    }
  };

  return (
    <div className="rounded-xl border border-border bg-background-elevated p-3.5 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-foreground">{item.label}</p>
            <Badge tone={isUploaded ? 'success' : 'neutral'}>{isUploaded ? 'Uploaded' : 'Missing'}</Badge>
          </div>
          {isUploaded && item.document && (
            <p className="text-xs text-foreground-subtle mt-0.5 truncate">
              {item.document.original_name}
              {uploadedDate && ` · ${uploadedDate}`}
            </p>
          )}
        </div>

        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/jpg,application/pdf" className="hidden" onChange={handleFile} />
        <Button
          type="button"
          size="sm"
          variant={isUploaded ? 'secondary' : 'primary'}
          isLoading={busyUpload || isUploading}
          onClick={() => inputRef.current?.click()}
        >
          <UploadIcon className="h-3.5 w-3.5" />
          {isUploaded ? 'Replace' : 'Upload'}
        </Button>
      </div>

      {/* Deliberately its own full-width, clearly-labeled action — not a
          small inline link next to Upload — so it's obvious at a glance
          that a fillable template is available here. */}
      {hasTemplate && (
        <Button type="button" size="sm" variant="secondary" className="w-full" isLoading={busyDownload} onClick={handleDownload}>
          <DownloadIcon className="h-3.5 w-3.5" />
          Download template
        </Button>
      )}
    </div>
  );
}
